package com.mps.asc_app.presentation

import com.mps.asc_app.data.auth.AuthRepository
import com.mps.asc_app.data.bookings.BookingKind
import com.mps.asc_app.data.bookings.BookingScope
import com.mps.asc_app.data.bookings.Bookings
import com.mps.asc_app.data.bookings.BookingsRepository
import com.mps.asc_app.data.bookings.RecurringBlockEntry
import com.mps.asc_app.data.config.ConfigRepository
import com.mps.asc_app.data.config.TimeslotConfig
import com.mps.asc_app.domain.bookings.canBookAdditionalFutureSlot
import com.mps.asc_app.domain.slots.buildTimeSlots
import com.mps.asc_app.ui.components.UiRow
import com.mps.asc_app.ui.components.UiSlot
import com.mps.asc_app.ui.components.UiSlotStatus
import com.mps.asc_app.util.TodayDate
import com.mps.asc_app.util.endOfDayEpochMillis
import com.mps.asc_app.util.epochMillisToLocalTimeLabel
import com.mps.asc_app.util.startOfDayEpochMillis
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlin.time.Clock
import kotlin.time.ExperimentalTime


data class BookingUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val config: TimeslotConfig? = null,
    /** Wird vom Screen gesetzt (aus MainViewModel), damit Admins weiterhin Namen sehen können. */
    val viewerIsAdmin: Boolean = false,
    val viewerIsTrainer: Boolean = false,
    val trainerAssignments: List<String> = emptyList(),
    val selectedDay: Int? = null,
    val selectedMonth: Int? = null,
    val selectedYear: Int? = null,
    val rows: List<UiRow> = emptyList(),
    val upcomingBookings: List<UpcomingBookingUi> = emptyList(),
)

data class UpcomingBookingUi(
    val bookingId: String,
    val title: String,
    val subtitle: String,
)

@OptIn(ExperimentalTime::class)
class BookingViewModel(
    private val configRepo: ConfigRepository,
    private val bookingsRepo: BookingsRepository,
    private val authRepository: AuthRepository,
    private val todayProvider: () -> TodayDate
) : BaseViewModel() {

    /** Muss vom UI gesetzt werden, da das ViewModel selbst keine Rollen kennt. */
    fun setViewerRole(isAdmin: Boolean, isTrainer: Boolean, trainerAssignments: List<String>) {
        val cleanedAssignments = trainerAssignments.map { it.trim() }.filter { it.isNotBlank() }.distinct()
        val current = _state.value
        if (
            current.viewerIsAdmin == isAdmin &&
            current.viewerIsTrainer == isTrainer &&
            current.trainerAssignments == cleanedAssignments
        ) return

        _state.update {
            it.copy(
                viewerIsAdmin = isAdmin,
                viewerIsTrainer = isTrainer,
                trainerAssignments = cleanedAssignments,
            )
        }
        rebuildRows()
    }

    private fun isBookingLimitExempt(): Boolean {
        val email = authRepository.currentUser()?.email?.trim()?.lowercase()
        return email == "marvinpetersen@icloud.com"
    }

    private val _state = MutableStateFlow(BookingUiState())
    val state: StateFlow<BookingUiState> = _state

    init {
        val today = todayProvider ()

        _state.update {
            it.copy(
                selectedYear = today.year,
                selectedMonth = today.month,
                selectedDay = today.day
            )
        }

        loadConfig()
        refreshUpcomingBookings()
    }

    fun loadConfig() {
        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            runCatching { configRepo.getTimeslotConfig() }
                .onSuccess { cfg ->
                    _state.update { it.copy(isLoading = false, config = cfg) }
                    rebuildRows()
                    refreshUpcomingBookings()
                }
                .onFailure { e ->
                    e.printStackTrace()
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = e.message ?: "Unbekannter Fehler"
                        )
                    }
                }
        }
    }

    fun onDaySelected(day: Int, month: Int, year: Int) {
        _state.update {
            it.copy(
                selectedDay = day,
                selectedMonth = month,
                selectedYear = year,
                isLoading = true,
                error = null
            )
        }
        rebuildRows()
        refreshUpcomingBookings()
    }

    private fun courtLabelFromCourtId(courtId: String): String {
        return when (courtId.trim().lowercase()) {
            "court1" -> "Platz 1"
            "court2" -> "Platz 2"
            else -> courtId
        }
    }

    private fun parseUpcomingLabelFromId(
        bookingId: String,
        fallbackEpochMillis: Long,
        fallbackCourtId: String,
    ): Pair<String, String> {
        // Gewünschtes/Legacy-Format: court1_2025-08-24_08:00
        val underscoreParts = bookingId.split("_")
        if (underscoreParts.size >= 3) {
            val court = underscoreParts[0]
            val date = underscoreParts[1]
            val time = underscoreParts[2]

            val dateParts = date.split("-")
            if (dateParts.size == 3) {
                val year = dateParts[0]
                val month = dateParts[1]
                val day = dateParts[2]
                val title = "${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year} $time"
                return title to courtLabelFromCourtId(court)
            }
        }

        // Altes Zwischenformat (neu): YYYY-M-D-court1-10:00
        val dashParts = bookingId.split("-")
        if (dashParts.size >= 5) {
            val year = dashParts[0].toIntOrNull()
            val month = dashParts[1].toIntOrNull()
            val day = dashParts[2].toIntOrNull()
            val court = dashParts[3]
            if (year != null && month != null && day != null) {
                val dd = day.toString().padStart(2, '0')
                val mm = month.toString().padStart(2, '0')
                val time = epochMillisToLocalTimeLabel(fallbackEpochMillis)
                return "$dd.$mm.$year $time" to courtLabelFromCourtId(court)
            }
        }

        // Fallback: Uhrzeit + Platz aus Dokument-Feld
        val time = epochMillisToLocalTimeLabel(fallbackEpochMillis)
        return time to courtLabelFromCourtId(fallbackCourtId)
    }

    private fun refreshUpcomingBookings() {
        val uid = authRepository.currentUser()?.uid ?: run {
            _state.update { it.copy(upcomingBookings = emptyList()) }
            return
        }

        val nowEpochMillis = Clock.System.now().toEpochMilliseconds()
        scope.launch {
            val bookingsResult = runCatching {
                bookingsRepo.getFutureBookingsForUser(
                    userId = uid,
                    userEmail = authRepository.currentUser()?.email,
                    nowEpochMillis = nowEpochMillis,
                    limit = 10,
                )
            }

            val bookings = bookingsResult.getOrElse { e ->
                _state.update { it.copy(error = e.message ?: "Kommende Buchungen konnten nicht geladen werden.") }
                emptyList()
            }

            val ui = bookings.map { booking ->
                val epoch = booking.slotsTs.toEpochMilliseconds()
                val (title, metaLabel) = parseUpcomingLabelFromId(booking.id, epoch, booking.courtId)
                UpcomingBookingUi(
                    bookingId = booking.id,
                    title = title,
                    subtitle = metaLabel,
                )
            }

            _state.update { it.copy(upcomingBookings = ui) }
        }
    }

    fun cancelUpcomingBooking(bookingId: String) {
        val uid = authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _state.update { it.copy(error = "Bitte erneut einloggen.") }
            return
        }
        if (bookingId.isBlank()) return

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val cancelled = runCatching { bookingsRepo.cancelBooking(bookingId, uid) }
                .getOrElse { false }

            if (!cancelled) {
                _state.update { it.copy(isLoading = false, error = "Storno fehlgeschlagen.") }
                return@launch
            }

            refreshUpcomingBookings()
            rebuildRows()
        }
    }

    fun blockSlot(slot: UiSlot, comment: String) {
        val uid = authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _state.update { it.copy(error = "Bitte erneut einloggen.") }
            return
        }
        val slotEpochMillis = slot.slotEpochMillis ?: run {
            _state.update { it.copy(error = "Ungueltiger Slot.") }
            return
        }
        val courtId = slot.courtId ?: ""
        if (courtId.isBlank()) {
            _state.update { it.copy(error = "Ungueltiger Platz.") }
            return
        }

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val result = runCatching {
                bookingsRepo.blockSlot(
                    bookingId = slot.id,
                    courtId = courtId,
                    slotEpochMillis = slotEpochMillis,
                    adminUserId = uid,
                    comment = comment,
                    trainingGroupId = null,
                    recurringRule = null,
                )
            }

            result.onFailure { e ->
                _state.update { it.copy(isLoading = false, error = e.message ?: "Blockieren fehlgeschlagen.") }
                return@launch
            }

            val ok = result.getOrThrow()
            if (!ok) {
                _state.update { it.copy(isLoading = false, error = "Slot ist bereits belegt/gesperrt." ) }
                return@launch
            }

            refreshUpcomingBookings()
            rebuildRows()
        }
    }

    fun unblockSlot(slot: UiSlot) {
        if (!slot.isAdminBlocked) return
        val uid = authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _state.update { it.copy(error = "Bitte erneut einloggen.") }
            return
        }

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            val result = runCatching { bookingsRepo.unblockSlot(slot.id) }
            result.onFailure { e ->
                // Wichtig, um Permission-Denied / Missing-Index etc. sichtbar zu machen
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = e.message ?: "Sperre konnte nicht aufgehoben werden."
                    )
                }
                return@launch
            }

            val ok = result.getOrThrow()
            if (!ok) {
                _state.update { it.copy(isLoading = false, error = "Slot ist nicht (mehr) gesperrt.") }
                return@launch
            }
            refreshUpcomingBookings()
            rebuildRows()
        }
    }

    fun bookTrainingSlot(slot: UiSlot, trainingGroupId: String) {
        val uid = authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _state.update { it.copy(error = "Bitte erneut einloggen.") }
            return
        }

        val cleanedGroupId = trainingGroupId.trim()
        if (cleanedGroupId.isBlank()) {
            _state.update { it.copy(error = "Bitte Mannschaft/Jahrgang wählen.") }
            return
        }

        val roleState = _state.value
        if (!roleState.viewerIsAdmin && !roleState.viewerIsTrainer) {
            _state.update { it.copy(error = "Nur Trainer/Admin können Trainingsbuchungen anlegen.") }
            return
        }

        if (!roleState.viewerIsAdmin && cleanedGroupId !in roleState.trainerAssignments) {
            _state.update { it.copy(error = "Du bist dieser Mannschaft/diesem Jahrgang nicht zugewiesen.") }
            return
        }

        val slotEpochMillis = slot.slotEpochMillis ?: run {
            _state.update { it.copy(error = "Ungültiger Slot.") }
            return
        }

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val bookingCreated = runCatching {
                bookingsRepo.createBooking(
                    bookingId = slot.id,
                    courtId = slot.courtId ?: "",
                    slotEpochMillis = slotEpochMillis,
                    userId = uid,
                    displayNameSnapshot = "Training",
                    bookingScope = BookingScope.TRAINING,
                    trainingGroupId = cleanedGroupId,
                )
            }.getOrElse { e ->
                _state.update { it.copy(isLoading = false, error = e.message ?: "Trainingsbuchung fehlgeschlagen.") }
                return@launch
            }

            if (!bookingCreated) {
                _state.update { it.copy(isLoading = false, error = "Der Slot ist leider schon belegt.") }
                return@launch
            }

            refreshUpcomingBookings()
            rebuildRows()
        }
    }

    fun createRecurringTrainingBlocks(slot: UiSlot, trainingGroupId: String, weeks: Int, comment: String) {
        val uid = authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _state.update { it.copy(error = "Bitte erneut einloggen.") }
            return
        }

        val cleanedGroupId = trainingGroupId.trim()
        if (cleanedGroupId.isBlank()) {
            _state.update { it.copy(error = "Bitte Mannschaft/Jahrgang wählen.") }
            return
        }

        val roleState = _state.value
        if (!roleState.viewerIsAdmin && !roleState.viewerIsTrainer) {
            _state.update { it.copy(error = "Nur Trainer/Admin können Regeltermine anlegen.") }
            return
        }
        if (!roleState.viewerIsAdmin && cleanedGroupId !in roleState.trainerAssignments) {
            _state.update { it.copy(error = "Du bist dieser Mannschaft/diesem Jahrgang nicht zugewiesen.") }
            return
        }

        val plannedWeeks = weeks.coerceIn(1, 52)
        val parsed = parseSlotId(slot.id)
        if (parsed == null) {
            _state.update { it.copy(error = "Regeltermine sind für diesen Slot nicht verfügbar.") }
            return
        }

        val minutesOfDay = parseMinutesOfDay(parsed.time) ?: run {
            _state.update { it.copy(error = "Ungültige Uhrzeit im Slot.") }
            return
        }

        val recurringEntries = (0 until plannedWeeks).map { index ->
            val nextDate = parsed.date.plus(index * 7, DateTimeUnit.DAY)
            val dayStart = startOfDayEpochMillis(nextDate.year, nextDate.monthNumber, nextDate.dayOfMonth)
            val slotEpoch = dayStart + minutesOfDay * 60_000L
            RecurringBlockEntry(
                bookingId = "${parsed.courtId}_${nextDate}_${parsed.time}",
                courtId = parsed.courtId,
                slotEpochMillis = slotEpoch,
                recurringRule = "weekly:$plannedWeeks",
            )
        }

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val created = runCatching {
                bookingsRepo.blockRecurringSlots(
                    entries = recurringEntries,
                    actorUserId = uid,
                    comment = comment.ifBlank { "Mannschaftstraining" },
                    trainingGroupId = cleanedGroupId,
                )
            }.getOrElse { e ->
                _state.update { it.copy(isLoading = false, error = e.message ?: "Regeltermine konnten nicht angelegt werden.") }
                return@launch
            }

            if (created <= 0) {
                _state.update { it.copy(isLoading = false, error = "Keine Regeltermine angelegt (bereits belegt/gesperrt).") }
                return@launch
            }

            refreshUpcomingBookings()
            rebuildRows()
        }
    }

    private data class ParsedSlotId(
        val courtId: String,
        val date: LocalDate,
        val time: String,
    )

    private fun parseSlotId(slotId: String): ParsedSlotId? {
        val parts = slotId.split("_")
        if (parts.size != 3) return null
        val courtId = parts[0].trim()
        val date = runCatching { LocalDate.parse(parts[1].trim()) }.getOrNull() ?: return null
        val time = normalizeTimeLabel(parts[2])
        if (courtId.isBlank() || parseMinutesOfDay(time) == null) return null
        return ParsedSlotId(courtId = courtId, date = date, time = time)
    }

    private fun normalizeTimeLabel(value: String): String {
        val parts = value.split(":")
        if (parts.size != 2) return value.trim()

        val hour = parts[0].trim().toIntOrNull() ?: return value.trim()
        val minute = parts[1].trim().toIntOrNull() ?: return value.trim()

        val normalizedHour = hour.toString().padStart(2, '0')
        val normalizedMinute = minute.toString().padStart(2, '0')
        return "$normalizedHour:$normalizedMinute"
    }

    private fun bookingForSlot(slotStart: String, bookings: List<Bookings>): Bookings? {
        val normalizedSlotStart = normalizeTimeLabel(slotStart)

        return bookings.firstOrNull { booking ->
            val bookingTime = normalizeTimeLabel(
                epochMillisToLocalTimeLabel(booking.slotsTs.toEpochMilliseconds())
            )
            bookingTime == normalizedSlotStart
        }
    }

    private fun resolveParticipantNames(booking: Bookings?): List<String> {
        if (booking == null) return emptyList()
        return booking.bookingplayers
            .map { player ->
                val name = player.displayNameSnapshot?.trim().orEmpty()
                when (player.privacyConsentAcceptedSnapshot) {
                    true -> name.ifBlank { "Belegt" }
                    false -> "Belegt"
                    null -> {
                        // Legacy-Fallback: Bei fehlendem Consent-Snapshot vorhandenen Namen verwenden.
                        if (name.isNotBlank() && !name.equals("belegt", ignoreCase = true)) name else "Belegt"
                    }
                }
            }
            .ifEmpty { listOf("Belegt") }
    }

    private fun visibleParticipantNames(
        booking: Bookings?,
        viewerIsAdmin: Boolean,
        bookingNamesPublic: Boolean,
        isOwnBooking: Boolean,
    ): List<String> {
        if (booking == null) return emptyList()
        // Sichtbarkeit basiert auf Datenschutz-Zustimmung je Spieler.
        return resolveParticipantNames(booking)
    }

    private fun parseMinutesOfDay(value: String): Int? {
        val parts = value.split(":")
        if (parts.size != 2) return null
        val hour = parts[0].trim().toIntOrNull() ?: return null
        val minute = parts[1].trim().toIntOrNull() ?: return null
        if (hour !in 0..23 || minute !in 0..59) return null
        return hour * 60 + minute
    }

    private fun buildSlotDocumentId(
        year: Int,
        month: Int,
        day: Int,
        courtId: String,
        slotStart: String
    ): String {
        // Gewünschtes Format: court1_2025-08-24_08:00
        val mm = month.toString().padStart(2, '0')
        val dd = day.toString().padStart(2, '0')
        val time = normalizeTimeLabel(slotStart)
        return "${courtId}_${year}-${mm}-${dd}_${time}"
    }

    fun onSlotClicked(slot: UiSlot) {
        val uid = authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _state.update { it.copy(error = "Bitte erneut einloggen.") }
            return
        }

        val slotEpochMillis = slot.slotEpochMillis
        if (slotEpochMillis == null) {
            _state.update { it.copy(error = "Ungultiger Slot.") }
            return
        }

        val nowEpochMillis = Clock.System.now().toEpochMilliseconds()
        if (slotEpochMillis <= nowEpochMillis && slot.status != UiSlotStatus.BOOKED) {
            _state.update { it.copy(error = "Vergangene Slots konnen nicht gebucht werden.") }
            return
        }

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            when (slot.status) {
                UiSlotStatus.FREE -> {
                    if (!isBookingLimitExempt()) {
                        val activeFutureBookings = runCatching {
                            bookingsRepo.countFutureBookingsForUser(uid, nowEpochMillis)
                        }.getOrElse { e ->
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    error = e.message ?: "Fehler beim Prufen deines Buchungslimits."
                                )
                            }
                            return@launch
                        }

                        if (!canBookAdditionalFutureSlot(activeFutureBookings)) {
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    error = "Du hast bereits 3 zukunftige Buchungen. Storniere oder warte, bis ein Slot abgelaufen ist."
                                )
                            }
                            return@launch
                        }
                    }

                    val bookingCreated = runCatching {
                        bookingsRepo.createBooking(
                            bookingId = slot.id,
                            courtId = slot.courtId ?: "",
                            slotEpochMillis = slotEpochMillis,
                            userId = uid,
                            displayNameSnapshot = null,
                            bookingScope = BookingScope.SELF,
                            trainingGroupId = null,
                        )
                    }.getOrElse { e ->
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = e.message ?: "Buchung fehlgeschlagen."
                            )
                        }
                        return@launch
                    }

                    if (!bookingCreated) {
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = "Der Slot ist leider schon belegt."
                            )
                        }
                        return@launch
                    }
                }

                UiSlotStatus.BOOKED -> {
                    if (!slot.canCancel) {
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = "Du kannst nur deine eigenen Buchungen stornieren."
                            )
                        }
                        return@launch
                    }

                    val cancelled = runCatching {
                        bookingsRepo.cancelBooking(
                            bookingId = slot.id,
                            userId = uid
                        )
                    }.getOrElse { e ->
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = e.message ?: "Storno fehlgeschlagen."
                            )
                        }
                        return@launch
                    }

                    if (!cancelled) {
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = "Nur deine eigenen Buchungen konnen storniert werden."
                            )
                        }
                        return@launch
                    }
                }

                UiSlotStatus.BLOCKED -> {
                    val msg = when {
                        slot.isAdminBlocked -> {
                            val c = slot.blockedComment?.takeIf { it.isNotBlank() }
                            if (c != null) "Slot gesperrt: $c" else "Slot ist gesperrt."
                        }
                        else -> "Slot ist nicht buchbar."
                    }
                    _state.update { it.copy(isLoading = false, error = msg) }
                    return@launch
                }
            }

            refreshUpcomingBookings()
            rebuildRows()
        }
    }

    private fun rebuildRows() {
        val currentState = _state.value
        val cfg = currentState.config ?: return
        val day = currentState.selectedDay ?: return
        val month = currentState.selectedMonth ?: return
        val year = currentState.selectedYear ?: return

        val bookingNamesPublic = cfg.bookingNamesPublic
        val viewerIsAdmin = currentState.viewerIsAdmin

        val start = startOfDayEpochMillis(year, month, day)
        val end = endOfDayEpochMillis(year, month, day)

        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            val bookings1Result = runCatching {
                bookingsRepo.getBookingsForDay(
                    courtId = "court1",
                    dayStartEpochMillis = start,
                    dayEndEpochMillis = end
                )
            }

            val bookings2Result = runCatching {
                bookingsRepo.getBookingsForDay(
                    courtId = "court2",
                    dayStartEpochMillis = start,
                    dayEndEpochMillis = end
                )
            }

            val bookings1 = bookings1Result.getOrElse { error ->
                error.printStackTrace()
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = error.message ?: "Fehler beim Laden der Buchungen für Platz 1"
                    )
                }
                return@launch
            }

            val bookings2 = bookings2Result.getOrElse { error ->
                error.printStackTrace()
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = error.message ?: "Fehler beim Laden der Buchungen für Platz 2"
                    )
                }
                return@launch
            }

            val slots = buildTimeSlots(cfg)
            val nowEpochMillis = Clock.System.now().toEpochMilliseconds()
            val currentUserId = authRepository.currentUser()?.uid

            val rows = slots.map { slot ->
                val bookingForSlot1 = bookingForSlot(slot.start, bookings1)
                val bookingForSlot2 = bookingForSlot(slot.start, bookings2)

                val minutesOfDay = parseMinutesOfDay(slot.start) ?: 0
                val slotEpochMillis = start + minutesOfDay * 60_000L

                val hasEntry1 = bookingForSlot1 != null
                val isAdminBlocked1 = bookingForSlot1?.kind == BookingKind.BLOCKED
                val isBooked1 = hasEntry1 && !isAdminBlocked1
                val isPast1 = slotEpochMillis <= nowEpochMillis && !hasEntry1
                val isOwnBooking1 = isBooked1 && (
                    bookingForSlot1?.ownerUserId == currentUserId ||
                        bookingForSlot1?.bookingplayers?.any { it.userId == currentUserId } == true
                    )
                val canCancel1 = isBooked1 && isOwnBooking1

                val visibleNames1 = visibleParticipantNames(
                    booking = bookingForSlot1,
                    viewerIsAdmin = viewerIsAdmin,
                    bookingNamesPublic = bookingNamesPublic,
                    isOwnBooking = isOwnBooking1,
                )

                val hasEntry2 = bookingForSlot2 != null
                val isAdminBlocked2 = bookingForSlot2?.kind == BookingKind.BLOCKED
                val isBooked2 = hasEntry2 && !isAdminBlocked2
                val isPast2 = slotEpochMillis <= nowEpochMillis && !hasEntry2
                val isOwnBooking2 = isBooked2 && (
                    bookingForSlot2?.ownerUserId == currentUserId ||
                        bookingForSlot2?.bookingplayers?.any { it.userId == currentUserId } == true
                    )
                val canCancel2 = isBooked2 && isOwnBooking2

                val visibleNames2 = visibleParticipantNames(
                    booking = bookingForSlot2,
                    viewerIsAdmin = viewerIsAdmin,
                    bookingNamesPublic = bookingNamesPublic,
                    isOwnBooking = isOwnBooking2,
                )

                UiRow(
                    timeLabel = slot.start,
                    left = UiSlot(
                        id = bookingForSlot1?.id
                            ?: buildSlotDocumentId(year, month, day, "court1", slot.start),
                        status = when {
                            isBooked1 -> UiSlotStatus.BOOKED
                            isAdminBlocked1 || isPast1 -> UiSlotStatus.BLOCKED
                            else -> UiSlotStatus.FREE
                        },
                        title = when {
                            isPast1 -> "Abgelaufen"
                            isAdminBlocked1 -> "Gesperrt"
                            isBooked1 -> "Belegt"
                            else -> "Frei"
                        },
                        subtitle = when {
                            isBooked1 && canCancel1 -> "Tippen zum Stornieren"
                            isBooked1 -> "Bereits belegt"
                            isAdminBlocked1 -> bookingForSlot1?.blockedComment?.takeIf { it.isNotBlank() }
                                ?: "Gesperrt"
                            isPast1 -> null
                            else -> "Tippen zum Buchen"
                        },
                        participantNames = visibleNames1,
                        courtId = "court1",
                        slotEpochMillis = bookingForSlot1?.slotsTs?.toEpochMilliseconds() ?: slotEpochMillis,
                        canCancel = canCancel1,
                        blockedComment = bookingForSlot1?.blockedComment,
                        isAdminBlocked = isAdminBlocked1,
                        canUnblock = isAdminBlocked1,
                    ),
                    right = UiSlot(
                        id = bookingForSlot2?.id
                            ?: buildSlotDocumentId(year, month, day, "court2", slot.start),
                        status = when {
                            isBooked2 -> UiSlotStatus.BOOKED
                            isAdminBlocked2 || isPast2 -> UiSlotStatus.BLOCKED
                            else -> UiSlotStatus.FREE
                        },
                        title = when {
                            isPast2 -> "Abgelaufen"
                            isAdminBlocked2 -> "Gesperrt"
                            isBooked2 -> "Belegt"
                            else -> "Frei"
                        },
                        subtitle = when {
                            isBooked2 && canCancel2 -> "Tippen zum Stornieren"
                            isBooked2 -> "Bereits belegt"
                            isAdminBlocked2 -> bookingForSlot2?.blockedComment?.takeIf { it.isNotBlank() }
                                ?: "Gesperrt"
                            isPast2 -> null
                            else -> "Tippen zum Buchen"
                        },
                        participantNames = visibleNames2,
                        courtId = "court2",
                        slotEpochMillis = bookingForSlot2?.slotsTs?.toEpochMilliseconds() ?: slotEpochMillis,
                        canCancel = canCancel2,
                        blockedComment = bookingForSlot2?.blockedComment,
                        isAdminBlocked = isAdminBlocked2,
                        canUnblock = isAdminBlocked2,
                    )
                )
            }

            _state.update {
                it.copy(
                    isLoading = false,
                    error = null,
                    rows = rows
                )
            }
        }
    }
}